import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { AssetTypeIdentifier, AssetTypeStatus, ProcedureStep, Stored, User } from "@overmap-ai/common"
import { ConditionManager, type Field, FieldSchema } from "@overmap-ai/forms"
import { Eye } from "lucide-react"
import { Fragment, memo, type ReactElement, useCallback, useMemo } from "react"

import { AssetTypeStatusFilterDef } from "../../filtering/defs/AssetTypeStatusFilterDef"
import { CreatedByFilterDef } from "../../filtering/defs/CreatedByFilterDef"
import { IdentifierFilterDef } from "../../filtering/defs/IdentifierFilterDef"
import { ProcedureStepAssigneeFilterDef } from "../../filtering/defs/ProcedureStepAssigneeFilterDef"
import { ProcedureStepCompletionStatusFilterDef } from "../../filtering/defs/ProcedureStepCompletionStatusFilterDef"
import { ProcedureStepReviewerFilterDef } from "../../filtering/defs/ProcedureStepReviewerFilterDef"
import { ProcedureStepReviewStatusFilterDef } from "../../filtering/defs/ProcedureStepReviewStatusFilterDef"
import { SubmittedAtFilterDef } from "../../filtering/defs/SubmittedAtFilterDef"
import { getFilterDefOfField } from "../../filtering/defs/utils"
import { FilterManager } from "../../filtering/manager"
import { modelIconMapping } from "../../mappings/icons"
import type { ProcedureInitiativeData } from "../../typings"
import { FieldGroup } from "../MenuGroups/FieldGroup"
import { IdentifierGroup } from "../MenuGroups/IdentifierGroup"

export interface ProcedureInitiativeDataFilterMenuProps extends MenuRootProps, MenuVariantProps {
	children: ReactElement
	filterManager: FilterManager<ProcedureInitiativeData>
	procedureTypeFieldSchema: FieldSchema
	procedureSteps: Stored<ProcedureStep>[]
	procedureStepFieldSchemasAndConditions: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
	users: User[]
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
}

export const ProcedureInitiativeDataFilterMenu = memo<ProcedureInitiativeDataFilterMenuProps>((props) => {
	const {
		children,
		variant,
		size,
		accentColor,
		filterManager,
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		users,
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
		...rest
	} = props

	const handleSelectCreatedBy = useCallback(() => {
		filterManager.addFilter(new CreatedByFilterDef(users).createFilter())
	}, [filterManager, users])

	const handleSelectSubmittedAt = useCallback(() => {
		filterManager.addFilter(new SubmittedAtFilterDef().createFilter())
	}, [filterManager])

	const handleSelectAssetTypeStatus = useCallback(() => {
		filterManager.addFilter(new AssetTypeStatusFilterDef(assetTypeStatuses).createFilter())
	}, [assetTypeStatuses, filterManager])

	const handleIdentifierSelect = useCallback(
		(identifier: Stored<AssetTypeIdentifier>) => {
			filterManager.addFilter(new IdentifierFilterDef(identifier).createFilter())
		},
		[filterManager],
	)

	const handleSelectProcedureStepCompletionStatus = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepCompletionStatusFilterDef(procedureStep).createFilter())
		},
		[filterManager],
	)

	const handleSelectProcedureStepReviewStatus = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepReviewStatusFilterDef(procedureStep).createFilter())
		},
		[filterManager],
	)

	const handleSelectProcedureStepAssignees = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepAssigneeFilterDef(procedureStep, users).createFilter())
		},
		[filterManager, users],
	)

	const handleSelectProcedureStepReviewers = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			filterManager.addFilter(new ProcedureStepReviewerFilterDef(procedureStep, users).createFilter())
		},
		[filterManager, users],
	)

	const handleFieldSelect = useCallback(
		(field: Field) => {
			filterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[filterManager],
	)

	const supportedAssetTypeFields = useMemo(() => assetTypeFieldSchema.getFields(), [assetTypeFieldSchema])

	const supportedProcedureTypeFields = useMemo(() => procedureTypeFieldSchema.getFields(), [procedureTypeFieldSchema])

	const supportedProcedureStepFields = useMemo(() => {
		const ret: Record<ProcedureStep["uuid"], Field[]> = {}

		for (const [uuid, [fields]] of Object.entries(procedureStepFieldSchemasAndConditions)) {
			ret[uuid] = fields.getFields()
		}

		return ret
	}, [procedureStepFieldSchemasAndConditions])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.Scroll>
					<Menu.Item onSelect={handleSelectCreatedBy}>
						<LuIcon icon={modelIconMapping.creator} />
						Created by
					</Menu.Item>
					<Menu.Item onSelect={handleSelectSubmittedAt}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Submitted at
					</Menu.Item>

					{assetTypeStatuses.length > 0 && (
						<Menu.Item onSelect={handleSelectAssetTypeStatus}>
							<LuIcon icon={modelIconMapping.status} />
							Asset status
						</Menu.Item>
					)}

					{assetTypeIdentifiers.length > 0 && (
						<>
							<Menu.Label>Asset identifiers</Menu.Label>
							<IdentifierGroup
								identifiers={assetTypeIdentifiers}
								onIdentifierSelect={handleIdentifierSelect}
							/>
						</>
					)}

					{supportedAssetTypeFields.length > 0 && (
						<>
							<Menu.Label>Asset fields</Menu.Label>
							<FieldGroup fields={supportedAssetTypeFields} onFieldSelect={handleFieldSelect} />
						</>
					)}

					{supportedProcedureTypeFields.length > 0 && (
						<>
							<Menu.Label>Procedure fields</Menu.Label>
							<FieldGroup fields={supportedProcedureTypeFields} onFieldSelect={handleFieldSelect} />
						</>
					)}

					{procedureSteps.map((procedureStep) => {
						const fields = supportedProcedureStepFields[procedureStep.uuid]

						return (
							<Fragment key={procedureStep.uuid}>
								<Menu.Label>{procedureStep.name}</Menu.Label>
								<Menu.Item onSelect={() => handleSelectProcedureStepCompletionStatus(procedureStep)}>
									<LuIcon icon={modelIconMapping.status} />
									Completion
								</Menu.Item>
								<Menu.Item onSelect={() => handleSelectProcedureStepReviewStatus(procedureStep)}>
									<LuIcon icon={Eye} />
									Review
								</Menu.Item>
								<Menu.Item onSelect={() => handleSelectProcedureStepAssignees(procedureStep)}>
									<LuIcon icon={modelIconMapping.assignee} />
									Assignees
								</Menu.Item>
								<Menu.Item onSelect={() => handleSelectProcedureStepReviewers(procedureStep)}>
									<LuIcon icon={modelIconMapping.reviewer} />
									Reviewers
								</Menu.Item>
								{fields.length > 0 && <FieldGroup fields={fields} onFieldSelect={handleFieldSelect} />}
							</Fragment>
						)
					})}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
ProcedureInitiativeDataFilterMenu.displayName = "ProcedureInitiativeDataFilterMenu"
